import { Link, useNavigate, useRouterState } from "@tanstack/react-router";
import { ChevronDown, Plus, Search, X, SlidersHorizontal, Check } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import {
  PRIMARY_TCGS,
  POPULAR_TCGS,
  ALL_TCGS_ALPHABETICAL,
  PRIMARY_SHORT,
  PRIMARY_TO_CANONICAL,
  tcgToParam,
  POPULAR_SPORTS,
  OTHER_SPORTS,
  ALL_SPORTS,
} from "@/lib/tcg-catalog";

type BrowseSearch = {
  q?: string;
  tcg?: string;
  cond?: "all" | "graded" | "raw";
  co?: "PSA" | "BGS" | "CGC" | "any";
  gmin?: string;
  gmax?: string;
  pmin?: string;
  pmax?: string;
  lang?: string;
  est?: string;
  sort?: "newest" | "price_desc" | "offers" | "fulfilled";
  set?: string;
};

// Language options mirror the buy-order posting form. "any" means
// "orders where the buyer did not restrict language".
const LANGUAGE_OPTIONS: { value: string; label: string }[] = [
  { value: "english", label: "English" },
  { value: "japanese", label: "Japanese" },
  { value: "chinese", label: "Chinese" },
  { value: "korean", label: "Korean" },
  { value: "other", label: "Other" },
  { value: "any", label: "Any (buyer unrestricted)" },
];

// URL tcg param is comma-separated to allow multi-select.
function parseTcgParam(v: string | undefined | null): string[] {
  if (!v) return [];
  return v
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
}
function joinTcgParam(list: string[]): string | undefined {
  if (!list.length) return undefined;
  return list.join(",");
}

function parseLangParam(v: string | undefined | null): string[] {
  if (!v) return [];
  return v
    .split(",")
    .map((s) => s.trim().toLowerCase())
    .filter(Boolean);
}
function joinLangParam(list: string[]): string | undefined {
  if (!list.length) return undefined;
  return list.join(",");
}

// A label matches an active URL value if either the raw label or its canonical
// form appears in the selected list.
function isActive(label: string, selected: string[]): boolean {
  if (selected.includes(label)) return true;
  const canonical = (PRIMARY_TO_CANONICAL as Record<string, string | null>)[label];
  return canonical != null && selected.includes(canonical);
}

export function CategoryNav() {
  const navigate = useNavigate();
  const search = useRouterState({ select: (s) => s.location.search as BrowseSearch });
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const selected = pathname === "/" ? parseTcgParam(search.tcg) : [];
  const selectedLangs = pathname === "/" ? parseLangParam(search.lang) : [];
  const activeFilterCount = selected.length + selectedLangs.length;

  const [moreOpen, setMoreOpen] = useState(false);
  const [sportsOpen, setSportsOpen] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  const moreCloseTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const sportsCloseTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    function onDoc(e: MouseEvent) {
      if (!wrapRef.current?.contains(e.target as Node)) {
        setMoreOpen(false);
        setSportsOpen(false);
        setFiltersOpen(false);
      }
    }
    if (moreOpen || sportsOpen || filtersOpen) document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [moreOpen, sportsOpen, filtersOpen]);

  useEffect(() => {
    if (drawerOpen) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = prev;
      };
    }
  }, [drawerOpen]);

  function selectSingle(label: string) {
    setMoreOpen(false);
    setSportsOpen(false);
    setDrawerOpen(false);
    setFiltersOpen(false);
    navigate({ to: "/", search: { tcg: tcgToParam(label) } });
  }

  function clearAll() {
    setMoreOpen(false);
    setSportsOpen(false);
    setDrawerOpen(false);
    setFiltersOpen(false);
    navigate({ to: "/", search: {} });
  }

  function applyMulti(nextList: string[], nextLangs: string[]) {
    const paramValue = joinTcgParam(nextList.map(tcgToParam));
    const langValue = joinLangParam(nextLangs);
    const nextSearch: Record<string, string> = { ...(search as Record<string, string>) };
    if (paramValue) nextSearch.tcg = paramValue;
    else delete nextSearch.tcg;
    if (langValue) nextSearch.lang = langValue;
    else delete nextSearch.lang;
    navigate({ to: "/", search: nextSearch });
  }

  function openMoreHover() {
    if (moreCloseTimer.current) clearTimeout(moreCloseTimer.current);
    setMoreOpen(true);
  }
  function scheduleMoreClose() {
    if (moreCloseTimer.current) clearTimeout(moreCloseTimer.current);
    moreCloseTimer.current = setTimeout(() => setMoreOpen(false), 120);
  }
  function openSportsHover() {
    if (sportsCloseTimer.current) clearTimeout(sportsCloseTimer.current);
    setSportsOpen(true);
  }
  function scheduleSportsClose() {
    if (sportsCloseTimer.current) clearTimeout(sportsCloseTimer.current);
    sportsCloseTimer.current = setTimeout(() => setSportsOpen(false), 120);
  }

  const anyMoreActive = useMemo(
    () => POPULAR_TCGS.some((t) => isActive(t, selected)),
    [selected],
  );
  const anySportActive = useMemo(
    () => ALL_SPORTS.some((s) => isActive(s, selected)),
    [selected],
  );

  return (
    <div className="border-b border-border bg-panel/70">
      {/* MOBILE: horizontally scrollable pills */}
      <div className="lg:hidden">
        <div className="mx-auto flex max-w-7xl gap-1.5 overflow-x-auto px-4 py-2 sm:px-6">
          {PRIMARY_TCGS.map((t) => (
            <button
              key={t}
              type="button"
              onClick={() => selectSingle(t)}
              className={`shrink-0 rounded-full border px-3 py-1.5 text-xs font-semibold whitespace-nowrap transition ${
                isActive(t, selected)
                  ? "border-primary/50 bg-primary/15 text-primary"
                  : "border-border bg-background/60 text-foreground/80 hover:text-foreground"
              }`}
            >
              {PRIMARY_SHORT[t]}
            </button>
          ))}
          <button
            type="button"
            onClick={() => setDrawerOpen(true)}
            className={`shrink-0 rounded-full border px-3 py-1.5 text-xs font-semibold whitespace-nowrap transition ${
              anySportActive
                ? "border-primary/50 bg-primary/15 text-primary"
                : "border-border bg-background/60 text-foreground/80"
            }`}
          >
            Sports ▾
          </button>
          <button
            type="button"
            onClick={() => setDrawerOpen(true)}
            className={`shrink-0 rounded-full border px-3 py-1.5 text-xs font-semibold whitespace-nowrap transition ${
              anyMoreActive
                ? "border-primary/50 bg-primary/15 text-primary"
                : "border-border bg-background/60 text-foreground/80"
            }`}
          >
            More ▾
          </button>
        </div>
      </div>

      {/* DESKTOP */}
      <div ref={wrapRef} className="relative hidden lg:block">
        <div className="mx-auto flex max-w-7xl items-center gap-1 px-4 sm:px-6">
          {PRIMARY_TCGS.map((t) => (
            <StripLink
              key={t}
              label={PRIMARY_SHORT[t]}
              active={isActive(t, selected)}
              onClick={() => selectSingle(t)}
            />
          ))}
          {/* Sports */}
          <div
            className="relative"
            onMouseEnter={openSportsHover}
            onMouseLeave={scheduleSportsClose}
          >
            <button
              type="button"
              onClick={() => setSportsOpen((v) => !v)}
              aria-expanded={sportsOpen}
              className={`group relative inline-flex items-center gap-1 px-3 py-2.5 text-sm font-semibold transition ${
                anySportActive
                  ? "text-primary"
                  : sportsOpen
                  ? "text-foreground"
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >
              <span>Sports</span>
              <ChevronDown className={`h-3.5 w-3.5 transition ${sportsOpen ? "rotate-180" : ""}`} />
              <span
                className={`pointer-events-none absolute inset-x-2 -bottom-px h-0.5 rounded-full transition ${
                  anySportActive
                    ? "bg-primary"
                    : sportsOpen
                    ? "bg-primary/60"
                    : "bg-transparent group-hover:bg-border"
                }`}
              />
            </button>

            {sportsOpen && (
              <div
                onMouseEnter={openSportsHover}
                onMouseLeave={scheduleSportsClose}
                className="absolute left-1/2 top-full z-40 w-[660px] -translate-x-1/2 pt-1 animate-fade-in"
              >
                <div className="rounded-md border border-border bg-panel p-6 shadow-2xl">
                  <div className="mb-3 text-[10px] font-bold uppercase tracking-[0.18em] text-muted-foreground">
                    Popular Sports
                  </div>
                  <ul className="grid grid-cols-2 gap-x-6 gap-y-0.5">
                    {POPULAR_SPORTS.map((s) => {
                      const active = isActive(s, selected);
                      return (
                        <li key={s}>
                          <button
                            type="button"
                            onClick={() => selectSingle(s)}
                            className={`w-full rounded px-2 py-1 text-left text-sm transition ${
                              active
                                ? "bg-primary/15 text-primary"
                                : "text-foreground/85 hover:bg-primary/10 hover:text-primary"
                            }`}
                          >
                            {s}
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                  <div className="mb-3 mt-5 text-[10px] font-bold uppercase tracking-[0.18em] text-muted-foreground">
                    All Other Sports
                  </div>
                  <ul className="grid grid-cols-2 gap-x-6 gap-y-0.5">
                    {OTHER_SPORTS.map((s) => {
                      const active = isActive(s, selected);
                      return (
                        <li key={s}>
                          <button
                            type="button"
                            onClick={() => selectSingle(s)}
                            className={`w-full rounded px-2 py-1 text-left text-sm transition ${
                              active
                                ? "bg-primary/15 text-primary"
                                : "text-foreground/85 hover:bg-primary/10 hover:text-primary"
                            }`}
                          >
                            {s}
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                  <div className="mt-5 flex items-center justify-between border-t border-border pt-4">
                    <Link
                      to="/create-order"
                      onClick={() => setSportsOpen(false)}
                      className="btn-primary ml-auto inline-flex items-center gap-1.5 px-3 py-1.5 text-xs"
                    >
                      <Plus className="h-3.5 w-3.5" /> Post a Buy Order
                    </Link>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* More Games */}
          <div
            className="relative"
            onMouseEnter={openMoreHover}
            onMouseLeave={scheduleMoreClose}
          >
            <button
              type="button"
              onClick={() => setMoreOpen((v) => !v)}
              aria-expanded={moreOpen}
              className={`group relative inline-flex items-center gap-1 px-3 py-2.5 text-sm font-semibold transition ${
                anyMoreActive
                  ? "text-primary"
                  : moreOpen
                  ? "text-foreground"
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >
              <span>More Games</span>
              <ChevronDown className={`h-3.5 w-3.5 transition ${moreOpen ? "rotate-180" : ""}`} />
              <span
                className={`pointer-events-none absolute inset-x-2 -bottom-px h-0.5 rounded-full transition ${
                  anyMoreActive
                    ? "bg-primary"
                    : moreOpen
                    ? "bg-primary/60"
                    : "bg-transparent group-hover:bg-border"
                }`}
              />
            </button>

            {moreOpen && (
              <div
                onMouseEnter={openMoreHover}
                onMouseLeave={scheduleMoreClose}
                className="absolute left-1/2 top-full z-40 w-[660px] -translate-x-1/2 pt-1 animate-fade-in"
              >
                <div className="rounded-md border border-border bg-panel p-6 shadow-2xl">
                  <div className="mb-3 text-[10px] font-bold uppercase tracking-[0.18em] text-muted-foreground">
                    Popular Games
                  </div>
                  <ul className="grid grid-cols-2 gap-x-6 gap-y-0.5">
                    {POPULAR_TCGS.map((t) => {
                      const active = isActive(t, selected);
                      return (
                        <li key={t}>
                          <button
                            type="button"
                            onClick={() => selectSingle(t)}
                            className={`w-full rounded px-2 py-1 text-left text-sm transition ${
                              active
                                ? "bg-primary/15 text-primary"
                                : "text-foreground/85 hover:bg-primary/10 hover:text-primary"
                            }`}
                          >
                            {t}
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                  <div className="mt-5 flex items-center justify-between border-t border-border pt-4">
                    <button
                      type="button"
                      onClick={clearAll}
                      className="text-xs font-bold uppercase tracking-wider text-primary hover:underline"
                    >
                      All Games →
                    </button>
                    <Link
                      to="/create-order"
                      onClick={() => setMoreOpen(false)}
                      className="btn-primary inline-flex items-center gap-1.5 px-3 py-1.5 text-xs"
                    >
                      <Plus className="h-3.5 w-3.5" /> Post a Buy Order
                    </Link>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Spacer + Filters trigger */}
          <div className="ml-auto">
            <button
              type="button"
              onClick={() => setFiltersOpen((v) => !v)}
              aria-expanded={filtersOpen}
              className={`inline-flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-xs font-semibold transition ${
                filtersOpen || activeFilterCount > 0
                  ? "border-primary/50 bg-primary/10 text-primary"
                  : "border-border bg-background/40 text-muted-foreground hover:text-foreground"
              }`}
            >
              <SlidersHorizontal className="h-3.5 w-3.5" />
              Filters {activeFilterCount > 0 ? `(${activeFilterCount})` : ""}
              <ChevronDown className={`h-3 w-3 transition ${filtersOpen ? "rotate-180" : ""}`} />
            </button>
          </div>
        </div>

        {filtersOpen && (
          <div className="absolute right-4 top-full z-40 pt-1 sm:right-6 animate-fade-in">
            <FiltersPanel
              selected={selected}
              selectedLangs={selectedLangs}
              onApply={(next, nextLangs) => {
                applyMulti(next, nextLangs);
                setFiltersOpen(false);
              }}
              onClose={() => setFiltersOpen(false)}
            />
          </div>
        )}
      </div>

      {/* MOBILE DRAWER */}
      {drawerOpen && (
        <MobileGameDrawer
          selected={selected}
          onPick={selectSingle}
          onAll={clearAll}
          onClose={() => setDrawerOpen(false)}
        />
      )}
    </div>
  );
}

function StripLink({
  label,
  active,
  onClick,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`group relative inline-flex items-center px-3 py-2.5 text-sm font-semibold transition ${
        active ? "text-primary" : "text-muted-foreground hover:text-foreground"
      }`}
    >
      <span>{label}</span>
      <span
        className={`pointer-events-none absolute inset-x-2 -bottom-px h-0.5 rounded-full transition ${
          active ? "bg-primary" : "bg-transparent group-hover:bg-border"
        }`}
      />
    </button>
  );
}

function FiltersPanel({
  selected,
  selectedLangs,
  onApply,
  onClose,
}: {
  selected: string[];
  selectedLangs: string[];
  onApply: (next: string[], nextLangs: string[]) => void;
  onClose: () => void;
}) {
  const [draft, setDraft] = useState<string[]>(() => selected.slice());
  const [langDraft, setLangDraft] = useState<string[]>(() => selectedLangs.slice());
  const [moreExpanded, setMoreExpanded] = useState<boolean>(() =>
    POPULAR_TCGS.some((t) => isActive(t, selected)),
  );

  function toggle(label: string) {
    setDraft((prev) => {
      const isOn = isActive(label, prev);
      if (isOn) {
        const param = tcgToParam(label);
        return prev.filter((v) => v !== label && v !== param);
      }
      return [...prev, label];
    });
  }

  function toggleLang(value: string) {
    setLangDraft((prev) =>
      prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value],
    );
  }

  const moreSelectedCount = POPULAR_TCGS.filter((t) => isActive(t, draft)).length;

  return (
    <div className="w-[320px] rounded-md border border-border bg-panel p-4 shadow-2xl">
      <div className="mb-3 flex items-center justify-between">
        <div className="font-display text-sm font-bold uppercase tracking-wider">Filters</div>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="text-muted-foreground hover:text-foreground"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      <ul className="space-y-1">
        {PRIMARY_TCGS.map((t) => {
          const on = isActive(t, draft);
          return (
            <li key={t}>
              <button
                type="button"
                onClick={() => toggle(t)}
                className={`flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-sm transition ${
                  on ? "bg-primary/15 text-primary" : "text-foreground/85 hover:bg-primary/10 hover:text-primary"
                }`}
              >
                <span
                  className={`flex h-4 w-4 shrink-0 items-center justify-center rounded border ${
                    on ? "border-primary bg-primary text-primary-foreground" : "border-border"
                  }`}
                >
                  {on && <Check className="h-3 w-3" />}
                </span>
                <span>{t}</span>
              </button>
            </li>
          );
        })}
      </ul>

      <div className="mt-2 border-t border-border pt-2">
        <button
          type="button"
          onClick={() => setMoreExpanded((v) => !v)}
          className="flex w-full items-center justify-between rounded px-2 py-1.5 text-left text-sm font-semibold text-muted-foreground hover:bg-muted hover:text-foreground"
        >
          <span>
            More {moreSelectedCount > 0 ? `(${moreSelectedCount} selected)` : ""}
          </span>
          <ChevronDown className={`h-3.5 w-3.5 transition ${moreExpanded ? "rotate-180" : ""}`} />
        </button>
        {moreExpanded && (
          <ul className="mt-1 max-h-64 space-y-0.5 overflow-y-auto pr-1 animate-fade-in">
            {POPULAR_TCGS.map((t) => {
              const on = isActive(t, draft);
              return (
                <li key={t}>
                  <button
                    type="button"
                    onClick={() => toggle(t)}
                    className={`flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-sm transition ${
                      on ? "bg-primary/15 text-primary" : "text-foreground/85 hover:bg-primary/10 hover:text-primary"
                    }`}
                  >
                    <span
                      className={`flex h-4 w-4 shrink-0 items-center justify-center rounded border ${
                        on ? "border-primary bg-primary text-primary-foreground" : "border-border"
                      }`}
                    >
                      {on && <Check className="h-3 w-3" />}
                    </span>
                    <span>{t}</span>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      <div className="mt-3 border-t border-border pt-3">
        <div className="mb-2 text-[10px] font-bold uppercase tracking-[0.18em] text-muted-foreground">
          Language
        </div>
        <ul className="grid grid-cols-2 gap-x-2 gap-y-0.5">
          {LANGUAGE_OPTIONS.map((opt) => {
            const on = langDraft.includes(opt.value);
            return (
              <li key={opt.value}>
                <button
                  type="button"
                  onClick={() => toggleLang(opt.value)}
                  className={`flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-sm transition ${
                    on ? "bg-primary/15 text-primary" : "text-foreground/85 hover:bg-primary/10 hover:text-primary"
                  }`}
                >
                  <span
                    className={`flex h-4 w-4 shrink-0 items-center justify-center rounded border ${
                      on ? "border-primary bg-primary text-primary-foreground" : "border-border"
                    }`}
                  >
                    {on && <Check className="h-3 w-3" />}
                  </span>
                  <span className="truncate">{opt.label}</span>
                </button>
              </li>
            );
          })}
        </ul>
        <p className="mt-1.5 px-2 text-[11px] leading-snug text-muted-foreground">
          Orders where the buyer didn't restrict language always appear in results.
        </p>
      </div>

      <div className="mt-4 flex items-center justify-between gap-2 border-t border-border pt-3">
        <button
          type="button"
          onClick={() => {
            setDraft([]);
            setLangDraft([]);
          }}
          className="text-xs font-semibold text-muted-foreground hover:text-foreground hover:underline"
        >
          Clear
        </button>
        <button
          type="button"
          onClick={() => onApply(draft, langDraft)}
          className="btn-primary inline-flex items-center gap-1.5 px-4 py-2 text-xs"
        >
          Apply
        </button>
      </div>
    </div>
  );
}

function MobileGameDrawer({
  selected,
  onPick,
  onAll,
  onClose,
}: {
  selected: string[];
  onPick: (label: string) => void;
  onAll: () => void;
  onClose: () => void;
}) {
  const [q, setQ] = useState("");
  const term = q.trim().toLowerCase();
  const games = useMemo(
    () => (term ? ALL_TCGS_ALPHABETICAL.filter((t) => t.toLowerCase().includes(term)) : ALL_TCGS_ALPHABETICAL),
    [term],
  );
  const sports = useMemo(
    () => (term ? (ALL_SPORTS as readonly string[]).filter((t) => t.toLowerCase().includes(term)) : ALL_SPORTS),
    [term],
  );
  const totalCount = games.length + sports.length;

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-background lg:hidden animate-fade-in">
      <div className="flex items-center justify-between border-b border-border px-4 py-3">
        <div className="font-display text-base font-bold">Choose a game</div>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="rounded-md p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground"
        >
          <X className="h-5 w-5" />
        </button>
      </div>
      <div className="border-b border-border p-3">
        <div className="relative">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <input
            autoFocus
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search games…"
            className="h-10 w-full rounded-md border border-border bg-panel/60 pl-9 pr-3 text-sm text-foreground placeholder:text-muted-foreground/60 focus:border-primary focus:outline-none"
          />
        </div>
      </div>
      <div className="flex-1 overflow-y-auto px-4 py-4">
        <button
          type="button"
          onClick={onAll}
          className={`mb-4 w-full rounded-md border px-3 py-2 text-left text-sm font-semibold transition ${
            selected.length === 0
              ? "border-primary/50 bg-primary/15 text-primary"
              : "border-border bg-panel/50 text-foreground/85"
          }`}
        >
          All Categories
        </button>

        {totalCount === 0 ? (
          <p className="mt-6 text-center text-sm text-muted-foreground">
            No categories match "{q}".
          </p>
        ) : (
          <>
            {games.length > 0 && (
              <>
                <div className="mb-1 mt-2 text-[10px] font-bold uppercase tracking-[0.18em] text-muted-foreground">
                  Games
                </div>
                <ul className="mb-3 space-y-0.5">
                  {games.map((t) => {
                    const active = isActive(t, selected);
                    return (
                      <li key={t}>
                        <button
                          type="button"
                          onClick={() => onPick(t)}
                          className={`w-full rounded px-3 py-2 text-left text-sm transition ${
                            active
                              ? "bg-primary/15 text-primary"
                              : "text-foreground/85 hover:bg-primary/10 hover:text-primary"
                          }`}
                        >
                          {t}
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </>
            )}
            {sports.length > 0 && (
              <>
                <div className="mb-1 mt-2 text-[10px] font-bold uppercase tracking-[0.18em] text-muted-foreground">
                  Sports
                </div>
                <ul className="space-y-0.5">
                  {sports.map((t) => {
                    const active = isActive(t, selected);
                    return (
                      <li key={t}>
                        <button
                          type="button"
                          onClick={() => onPick(t)}
                          className={`w-full rounded px-3 py-2 text-left text-sm transition ${
                            active
                              ? "bg-primary/15 text-primary"
                              : "text-foreground/85 hover:bg-primary/10 hover:text-primary"
                          }`}
                        >
                          {t}
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}
